import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Phone, 
  MapPin, 
  Wrench, 
  Tire, 
  CurrencyEur, 
  Clock,
  NavigationArrow,
  FileText,
  Envelope,
  Copy,
  X,
  Check,
  MapPinLine,
  Path,
  CalendarBlank,
  ClockCounterClockwise,
  Car,
  ChatCircle,
  Note,
  PencilSimple
} from '@phosphor-icons/react';
import { Input } from '../ui/input';
import StatusBadge from './StatusBadge';
import CountdownTimer from './CountdownTimer';

const API = process.env.REACT_APP_BACKEND_URL;

const canEditIntervention = (user, intervention) => {
  if (!user || !intervention) return false;
  const status = intervention.status;
  if (user.role === 'admin') return true;
  if (user.role === 'operateur') return ['en_attente', 'validee', 'en_route', 'en_cours'].includes(status);
  return false;
};

// Format date as "Lun. 14 avril — 14h30"
const formatFullDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const days = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  
  const dayName = days[date.getDay()];
  const dayNum = date.getDate();
  const month = months[date.getMonth()];
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${dayName} ${dayNum} ${month} — ${hours}h${minutes}`;
};

// Format time only
const formatTimeOnly = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return `${date.getHours()}h${String(date.getMinutes()).padStart(2, '0')}`;
};

export default function InterventionDetail({ intervention, onClose, onUpdate, onEdit }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clientHistory, setClientHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    if (intervention?.client_phone) {
      setClientHistory(null);
      setHistoryOpen(false);
    }
  }, [intervention?.id]);

  const fetchHistory = async () => {
    if (clientHistory) { setHistoryOpen(!historyOpen); return; }
    try {
      const response = await axios.get(
        `${API}/api/clients/${encodeURIComponent(intervention.client_phone)}/history`,
        { headers: getAuthHeaders() }
      );
      setClientHistory(response.data);
      setHistoryOpen(true);
    } catch {
      toast.error('Erreur chargement historique');
    }
  };

  const sendGeolocSms = async () => {
    if (!smsPhone) return;
    setSmsSending(true);
    try {
      const response = await axios.post(
        `${API}/api/interventions/${intervention.id}/send-geoloc-sms`,
        { phone: smsPhone, frontend_url: window.location.origin },
        { headers: getAuthHeaders() }
      );
      if (response.data.sent) {
        toast.success('SMS de géolocalisation envoyé');
      } else {
        toast.error(response.data.message);
      }
      setSmsOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi SMS');
    } finally {
      setSmsSending(false);
    }
  };

  if (!intervention) return null;

  const openWaze = () => {
    const encoded = encodeURIComponent(intervention.address);
    window.open(`https://waze.com/ul?q=${encoded}`, '_blank');
  };

  const downloadReceipt = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/interventions/${intervention.id}/receipt`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recu_${intervention.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reçu téléchargé');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/interventions/${intervention.id}/invoice`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${intervention.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Facture téléchargée');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setLoading(false);
    }
  };

  const copyGeolocLink = () => {
    const link = `${window.location.origin}/locate/${intervention.geoloc_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien de géolocalisation copié');
  };

  const copyInvoiceRequestLink = () => {
    const link = `${window.location.origin}/invoice-request/${intervention.invoice_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Lien de demande de facture copié');
  };

  const handleCancel = async () => {
    if (!window.confirm('Voulez-vous vraiment annuler cette intervention ?')) return;
    
    try {
      await axios.delete(
        `${API}/api/interventions/${intervention.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Intervention annulée');
      onUpdate?.();
      onClose();
    } catch (err) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const canGenerateDocuments = ['terminee', 'encaissee'].includes(intervention.status);
  const dimension = intervention.tire_dimension || 
    (intervention.tire_width && intervention.tire_height && intervention.tire_diameter 
      ? `${intervention.tire_width}/${intervention.tire_height}R${intervention.tire_diameter}` 
      : null);

  // Check if any technician tracking data exists
  const hasTrackingData = intervention.depart_at || intervention.arrival_at || 
    intervention.completed_at || intervention.cashed_at;

  return (
    <Dialog open={!!intervention} onOpenChange={() => onClose()}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl font-heading text-gray-900">
                {intervention.client_name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-2">
                <StatusBadge status={intervention.status} />
                <CountdownTimer
                  announcedTime={intervention.announced_time}
                  validatedAt={intervention.validated_at}
                  status={intervention.status}
                />
              </DialogDescription>
            </div>
            {canEditIntervention(user, intervention) && onEdit && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { onClose(); onEdit(intervention); }}
                className="text-gray-400 hover:text-reco-500 -mt-1"
                data-testid="edit-intervention-btn"
              >
                <PencilSimple size={20} />
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Scheduled Date/Time */}
          <div className="flex items-center gap-2 p-3 bg-reco-50 rounded-lg border border-reco-200">
            <CalendarBlank size={18} className="text-reco-600" />
            <span className="font-medium text-reco-700">{formatFullDateTime(intervention.announced_time)}</span>
          </div>

          {/* Contact */}
          <Section title="Contact">
            <InfoRow icon={<Phone size={16} />} label="Téléphone">
              <a href={`tel:${intervention.client_phone}`} className="text-reco-600 hover:underline">
                {intervention.client_phone}
              </a>
            </InfoRow>
            {intervention.client_email && (
              <InfoRow icon={<Envelope size={16} />} label="Email">
                {intervention.client_email}
              </InfoRow>
            )}
            <button
              onClick={fetchHistory}
              className="mt-2 flex items-center gap-1.5 text-xs text-reco-600 hover:text-reco-700 font-medium transition-colors"
              data-testid="client-history-toggle"
            >
              <ClockCounterClockwise size={14} />
              {historyOpen ? 'Masquer l\'historique' : 'Voir l\'historique client'}
            </button>
          </Section>

          {/* Client History */}
          {historyOpen && clientHistory && (
            <Section title={`Historique client (${clientHistory.total_interventions} interventions — ${clientHistory.total_spent.toLocaleString('fr-FR')} € encaissés)`}>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {clientHistory.interventions.filter(h => h.id !== intervention.id).map((h) => (
                  <div key={h.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800 truncate">{h.intervention_type}</span>
                        <StatusBadge status={h.status} />
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {h.created_at ? formatFullDateTime(h.created_at) : '-'}
                        {h.dimension && ` — ${h.dimension}`}
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 ml-3 whitespace-nowrap">
                      {h.price?.toFixed(2)} €
                    </span>
                  </div>
                ))}
                {clientHistory.interventions.filter(h => h.id !== intervention.id).length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-2">Aucune autre intervention</p>
                )}
              </div>
            </Section>
          )}

          {/* Intervention */}
          <Section title="Intervention">
            <InfoRow icon={<Wrench size={16} />} label="Type">
              {intervention.intervention_type}
            </InfoRow>
            <InfoRow icon={<MapPin size={16} />} label="Adresse">
              <div className="flex items-center gap-2">
                <span>{intervention.address}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={openWaze}
                  className="h-6 w-6 text-reco-500 hover:text-reco-600"
                  title="Ouvrir dans Waze"
                >
                  <NavigationArrow size={14} />
                </Button>
              </div>
            </InfoRow>
          </Section>

          {/* Pneus */}
          {(dimension || intervention.tire_type || intervention.tire_brand) && (
            <Section title="Pneus">
              {dimension && (
                <InfoRow icon={<Tire size={16} />} label="Dimension">
                  {dimension}
                </InfoRow>
              )}
              {intervention.tire_type && (
                <InfoRow label="Type">{intervention.tire_type}</InfoRow>
              )}
              {intervention.tire_brand && (
                <InfoRow label="Marque">{intervention.tire_brand} {intervention.tire_model || ''}</InfoRow>
              )}
              {intervention.condition_score && (
                <InfoRow label="État">{intervention.condition_score}/4</InfoRow>
              )}
              {intervention.selected_stock_quantity && (
                <InfoRow label="Quantité">{intervention.selected_stock_quantity}</InfoRow>
              )}
            </Section>
          )}

          {/* Véhicule */}
          {(intervention.vehicle_plate || intervention.vehicle_brand) && (
            <Section title="Véhicule">
              {intervention.vehicle_plate && (
                <InfoRow icon={<Car size={16} />} label="Immatriculation">{intervention.vehicle_plate}</InfoRow>
              )}
              {(intervention.vehicle_brand || intervention.vehicle_model) && (
                <InfoRow label="Véhicule">{intervention.vehicle_brand} {intervention.vehicle_model || ''}</InfoRow>
              )}
            </Section>
          )}

          {/* Source des pneus */}
          {intervention.tire_source && (
            <Section title="Source des pneus">
              <p className="text-sm font-medium text-gray-700">{intervention.tire_source}</p>
            </Section>
          )}

          {/* Technicien assigné */}
          {intervention.assigned_technician_name && (
            <Section title="Technicien assigné">
              <p className="text-sm font-medium text-gray-700">{intervention.assigned_technician_name}</p>
            </Section>
          )}

          {/* Notes */}
          {intervention.notes && (
            <Section title="Notes">
              <div className="flex items-start gap-2">
                <Note size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{intervention.notes}</p>
              </div>
            </Section>
          )}

          {/* Prix */}
          <Section title="Encaissement">
            <div className="flex items-center gap-2 text-2xl font-bold text-reco-600">
              <CurrencyEur size={24} />
              <span>{intervention.price?.toFixed(2)} €</span>
            </div>
          </Section>

          {/* Demande de facture — only when encaissée with a token */}
          {intervention.status === 'encaissee' && intervention.invoice_token && (
            <Section title="Demande de facture">
              <div className="space-y-3" data-testid="invoice-request-section">
                <div className="flex items-center gap-2">
                  {intervention.invoice_token_used ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-testid="invoice-status-used">
                      <Check size={12} weight="bold" />
                      Informations reçues — Facture générée
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="invoice-status-pending">
                      <Clock size={12} />
                      En attente de réponse client
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <a
                    href={`${window.location.origin}/invoice-request/${intervention.invoice_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-xs text-reco-600 hover:text-reco-700 underline break-all font-mono"
                    data-testid="invoice-request-link"
                  >
                    {`${window.location.origin}/invoice-request/${intervention.invoice_token}`}
                  </a>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyInvoiceRequestLink}
                    className="flex-shrink-0 gap-1.5 border-reco-500 text-reco-600 hover:bg-reco-50"
                    data-testid="copy-invoice-link"
                  >
                    <Copy size={14} />
                    Copier le lien
                  </Button>
                </div>
              </div>
            </Section>
          )}

          {/* Technician Tracking - NEW SECTION */}
          {hasTrackingData && (
            <Section title="Suivi technicien">
              <div className="space-y-3">
                <TrackingStep
                  label="Départ"
                  timestamp={intervention.depart_at}
                  lat={intervention.depart_lat}
                  lng={intervention.depart_lng}
                  address={intervention.depart_address}
                  technicianName={intervention.technician_name}
                />
                <TrackingStep
                  label="Arrivée"
                  timestamp={intervention.arrival_at}
                  lat={intervention.arrival_lat}
                  lng={intervention.arrival_lng}
                  address={intervention.arrival_address}
                />
                <TrackingStep
                  label="Fin d'intervention"
                  timestamp={intervention.completed_at}
                  lat={intervention.completed_lat}
                  lng={intervention.completed_lng}
                  address={intervention.completed_address}
                />
                <TrackingStep
                  label="Encaissement"
                  timestamp={intervention.cashed_at}
                  lat={intervention.cashed_lat}
                  lng={intervention.cashed_lng}
                  address={intervention.cashed_address}
                />
              </div>
            </Section>
          )}

          {/* Timeline (simplified) */}
          <Section title="Historique">
            <div className="space-y-2 text-sm">
              <TimelineItem label="Créée" time={intervention.created_at} />
              <TimelineItem label="Validée" time={intervention.validated_at} />
            </div>
          </Section>

          {/* Geoloc SMS */}
          {intervention.geoloc_token && !['terminee', 'encaissee', 'annulee'].includes(intervention.status) && (
            <Section title="Géolocalisation client">
              {!smsOpen ? (
                <Button
                  variant="outline"
                  onClick={() => { setSmsPhone(intervention.client_phone || ''); setSmsOpen(true); }}
                  className="w-full border-gray-300 hover:bg-gray-100 gap-2"
                  data-testid="send-geoloc-sms-btn"
                >
                  <ChatCircle size={16} />
                  Envoyer lien de position par SMS
                </Button>
              ) : (
                <div className="space-y-3">
                  <Input
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    placeholder="+33..."
                    className="bg-white border-gray-300"
                    data-testid="geoloc-sms-phone"
                  />
                  <div className="flex gap-2">
                    <Button onClick={sendGeolocSms} disabled={smsSending}
                      className="flex-1 bg-reco-500 hover:bg-reco-600 text-white gap-2" data-testid="geoloc-sms-send">
                      {smsSending ? 'Envoi...' : 'Envoyer'}
                    </Button>
                    <Button variant="outline" onClick={() => setSmsOpen(false)} className="border-gray-300">
                      Annuler
                    </Button>
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Document Actions */}
          {canGenerateDocuments && (
            <Section title="Documents">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={downloadReceipt}
                  disabled={loading}
                  className="border-gray-300 hover:bg-gray-100 gap-2"
                  data-testid="download-receipt"
                >
                  <FileText size={16} />
                  Reçu simple
                </Button>
                <Button
                  variant="outline"
                  onClick={downloadInvoice}
                  disabled={loading}
                  className="border-gray-300 hover:bg-gray-100 gap-2"
                  data-testid="download-invoice"
                >
                  <FileText size={16} />
                  Facture complète
                </Button>
              </div>
            </Section>
          )}

          {/* Cancel Button */}
          {!['terminee', 'encaissee', 'annulee'].includes(intervention.status) && (
            <Button
              variant="destructive"
              onClick={handleCancel}
              className="w-full gap-2"
              data-testid="cancel-intervention"
            >
              <X size={16} />
              Annuler l'intervention
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      {icon && <span className="text-gray-400 mt-0.5">{icon}</span>}
      <div className="flex-1">
        {label && <span className="text-gray-500 text-sm">{label}: </span>}
        <span className="text-gray-900">{children}</span>
      </div>
    </div>
  );
}

function TimelineItem({ label, time }) {
  if (!time) return null;
  const date = new Date(time);
  return (
    <div className="flex items-center justify-between text-gray-500">
      <span>{label}</span>
      <span className="text-gray-900">
        {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
}

function TrackingStep({ label, timestamp, lat, lng, address, technicianName }) {
  if (!timestamp) return null;
  
  const time = formatTimeOnly(timestamp);
  const hasLocation = lat && lng;
  
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
      <div className="mt-0.5">
        <Check size={18} weight="bold" className="text-reco-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-gray-900">{label}</span>
          <span className="text-gray-500">—</span>
          <span className="text-gray-700">{time}</span>
          {technicianName && (
            <span className="text-xs text-gray-400">({technicianName})</span>
          )}
        </div>
        
        {hasLocation ? (
          <div className="flex items-start gap-1.5">
            <MapPinLine size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-sm text-gray-600">
                {address || 'Adresse non disponible'}
              </span>
              <span className="text-xs text-gray-400 ml-2">
                ({lat.toFixed(4)}, {lng.toFixed(4)})
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-sm text-gray-400">
            <MapPinLine size={14} />
            <span>Position non disponible</span>
          </div>
        )}
      </div>
    </div>
  );
}
